/*
 * The uploads in flight from this tab, kept outside React so they outlive
 * the component that started them: moving between steps of a project does
 * not cancel a 15 GB transfer, and every view of the workspace reads the
 * same progress.
 *
 * Each entry mirrors one asset row (key = `${workspaceId}_${fileName}`).
 * Progress is reported to the API every few seconds so other tabs and
 * devices see the bar move too, and so the row reads "interrupted" rather
 * than "finalizing" if this tab goes away.
 */

import {
    GetVideoWorkspaceUploadUrl,
    GetVideoWorkspaceUploadStatus,
    ReportVideoWorkspaceUploadProgress,
} from '../../Client/ManagementServiceClient';
import {
    uploadToSession,
    UploadSessionGoneError,
    UploadAbortedError,
    DEFAULT_CHUNK_BYTES,
} from '../../Client/resumableUpload';

export const UPLOAD_PREPARING = 'preparing';
export const UPLOAD_UPLOADING = 'uploading';
export const UPLOAD_UPLOADED = 'uploaded';
export const UPLOAD_ERROR = 'error';
export const UPLOAD_CANCELLED = 'cancelled';

const MAX_CONCURRENT = 2;
const REPORT_EVERY_MS = 5000;
const PROGRESS_FRAME_MS = 250;

const uploads = new Map();
const listeners = new Set();
let snapshot = [];

export const uploadKey = (workspaceId, fileName) => `${workspaceId}_${fileName}`;

function notify() {
    snapshot = Array.from(uploads.values()).map((entry) => ({ ...entry }));
    listeners.forEach((listener) => {
        try { listener(snapshot); } catch { /* one bad listener must not stop the others */ }
    });
    syncUnloadGuard();
}

export function subscribeUploads(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getUploadsSnapshot() {
    return snapshot;
}

export function getUpload(key) {
    return uploads.get(key) || null;
}

export function dismissUpload(key) {
    const entry = uploads.get(key);
    if (!entry) return;
    if (entry.state === UPLOAD_PREPARING || entry.state === UPLOAD_UPLOADING) return;
    uploads.delete(key);
    notify();
}

export function cancelUpload(key) {
    const entry = uploads.get(key);
    if (!entry || !entry.controller) return;
    entry.controller.abort();
}

export function hasActiveUploads() {
    return Array.from(uploads.values()).some(
        (entry) => entry.state === UPLOAD_PREPARING || entry.state === UPLOAD_UPLOADING
    );
}

// Leaving the page kills every XHR; the session survives, so the upload can
// be resumed, but the user should get to decide that.
const warnBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = '';
    return '';
};
let unloadGuardArmed = false;
function syncUnloadGuard() {
    const wanted = hasActiveUploads();
    if (wanted === unloadGuardArmed || typeof window === 'undefined') return;
    if (wanted) window.addEventListener('beforeunload', warnBeforeUnload);
    else window.removeEventListener('beforeunload', warnBeforeUnload);
    unloadGuardArmed = wanted;
}

// At most a couple of transfers share the connection at once; the rest wait
// their turn in order.
let running = 0;
const waiting = [];
function acquireSlot() {
    if (running < MAX_CONCURRENT) {
        running += 1;
        return Promise.resolve();
    }
    return new Promise((resolve) => waiting.push(resolve));
}
function releaseSlot() {
    const next = waiting.shift();
    if (next) next();
    else running -= 1;
}

function update(key, patch) {
    const entry = uploads.get(key);
    if (!entry) return null;
    Object.assign(entry, patch);
    notify();
    return entry;
}

// Bytes-per-second smoothed over the last few samples, so the estimate does
// not jump with every packet.
function trackRate(entry, uploadedBytes, now) {
    if (!entry.rateSampleAt) {
        entry.rateSampleAt = now;
        entry.rateSampleBytes = uploadedBytes;
        return;
    }
    const elapsed = (now - entry.rateSampleAt) / 1000;
    if (elapsed < 1) return;
    const rate = Math.max(0, (uploadedBytes - entry.rateSampleBytes) / elapsed);
    entry.bytesPerSecond = entry.bytesPerSecond ? entry.bytesPerSecond * 0.7 + rate * 0.3 : rate;
    entry.rateSampleAt = now;
    entry.rateSampleBytes = uploadedBytes;
}

async function openSession({ workspaceId, file, alias, getIdToken }) {
    const idToken = await getIdToken();
    const response = await GetVideoWorkspaceUploadUrl(idToken, {
        VideoWorkspaceId: workspaceId,
        FileName: file.name,
        FileAlias: alias && String(alias).trim() ? alias : null,
        SizeBytes: file.size,
    });
    if (response.status !== 200) {
        throw new Error(response.myJson?.detail || 'Failed to get upload URL');
    }
    return {
        sessionUri: response.myJson.UploadUrl,
        fileName: response.myJson.FileName,
        chunkBytes: Number(response.myJson.ChunkSizeBytes) || DEFAULT_CHUNK_BYTES,
        offset: 0,
    };
}

async function findSession({ workspaceId, fileName, file, getIdToken }) {
    const idToken = await getIdToken();
    const response = await GetVideoWorkspaceUploadStatus(idToken, {
        VideoWorkspaceId: workspaceId,
        FileName: fileName,
    });
    if (response.status !== 200) {
        throw new Error(response.myJson?.detail || 'Could not check the upload');
    }
    const status = response.myJson || {};
    if (status.IsFinalized) return { finalized: true };
    if (!status.UploadUrl) return null; // nothing to resume; start over
    if (Number(status.UploadTotalBytes) !== file.size) {
        throw new Error(`That is a different file (${file.size} bytes, the upload was ${status.UploadTotalBytes})`);
    }
    return {
        sessionUri: status.UploadUrl,
        fileName,
        chunkBytes: Number(status.ChunkSizeBytes) || DEFAULT_CHUNK_BYTES,
        offset: Number(status.UploadedBytes) || 0,
    };
}

/*
 * Start (or resume) uploading `file` into a workspace. Resolves as soon as
 * the asset row exists and the transfer is under way — callers refresh the
 * asset list then and let the row show progress. `done` settles when the
 * transfer ends, always with the entry (its state says how it ended).
 */
export async function startUpload({ workspaceId, file, alias = null, fileName = null, getIdToken, resume = false }) {
    const provisionalKey = uploadKey(workspaceId, fileName || file.name);
    const existing = uploads.get(provisionalKey);
    if (existing && (existing.state === UPLOAD_PREPARING || existing.state === UPLOAD_UPLOADING)) {
        return { key: provisionalKey, done: existing.done };
    }

    const controller = new AbortController();
    let resolveDone;
    const done = new Promise((resolve) => { resolveDone = resolve; });
    const entry = {
        key: provisionalKey,
        workspaceId,
        fileName: fileName || file.name,
        alias,
        file,
        totalBytes: file.size,
        uploadedBytes: 0,
        state: UPLOAD_PREPARING,
        error: '',
        startedAt: Date.now(),
        bytesPerSecond: 0,
        rateSampleAt: 0,
        rateSampleBytes: 0,
        controller,
        done,
    };
    uploads.set(provisionalKey, entry);
    notify();

    const finish = (patch) => {
        const finished = update(entry.key, { ...patch, controller: null });
        resolveDone(finished ? { ...finished } : { ...entry, ...patch });
    };

    let session;
    try {
        session = resume && (fileName || file.name)
            ? await findSession({ workspaceId, fileName: fileName || file.name, file, getIdToken })
            : null;
        if (session && session.finalized) {
            finish({ state: UPLOAD_UPLOADED, uploadedBytes: file.size });
            return { key: entry.key, done };
        }
        if (!session) {
            session = await openSession({ workspaceId, file, alias, getIdToken });
        }
    } catch (err) {
        finish({ state: UPLOAD_ERROR, error: err.message || String(err) });
        throw err;
    }

    // The server names the object after the alias; re-key the entry if that
    // differs from the browser's file name so it lines up with the asset row.
    if (session.fileName && session.fileName !== entry.fileName) {
        uploads.delete(entry.key);
        entry.key = uploadKey(workspaceId, session.fileName);
        entry.fileName = session.fileName;
        uploads.set(entry.key, entry);
    }
    update(entry.key, { state: UPLOAD_UPLOADING, uploadedBytes: session.offset });

    const run = async () => {
        await acquireSlot();
        let lastReportAt = 0;
        let lastReported = -1;
        const frame = { notifiedAt: 0 };
        const report = async (bytes, force) => {
            const now = Date.now();
            if (!force && (now - lastReportAt < REPORT_EVERY_MS || bytes === lastReported)) return;
            lastReportAt = now;
            lastReported = bytes;
            try {
                const idToken = await getIdToken();
                await ReportVideoWorkspaceUploadProgress(idToken, {
                    VideoWorkspaceId: workspaceId,
                    FileName: entry.fileName,
                    UploadedBytes: bytes,
                });
            } catch { /* progress reporting is best effort */ }
        };
        const recoverOffset = async () => {
            const found = await findSession({ workspaceId, fileName: entry.fileName, file, getIdToken });
            if (!found) throw new UploadSessionGoneError();
            if (found.finalized) return file.size;
            return found.offset;
        };

        let restarted = false;
        try {
            while (true) {
                try {
                    await uploadToSession({
                        sessionUri: session.sessionUri,
                        file,
                        offset: session.offset,
                        chunkBytes: session.chunkBytes,
                        signal: controller.signal,
                        onProgress: (bytes) => {
                            const current = uploads.get(entry.key);
                            if (!current) return;
                            const now = Date.now();
                            trackRate(current, bytes, now);
                            // XHR fires progress many times a second; the UI
                            // only needs a few frames of it.
                            if (bytes < file.size && now - frame.notifiedAt < PROGRESS_FRAME_MS) {
                                current.uploadedBytes = bytes;
                                return;
                            }
                            frame.notifiedAt = now;
                            update(entry.key, { uploadedBytes: bytes });
                        },
                        onCommitted: (bytes) => { report(bytes, bytes >= file.size); },
                        recoverOffset,
                    });
                    break;
                } catch (err) {
                    if (err instanceof UploadSessionGoneError && !restarted) {
                        // A week-old or discarded session: open a fresh one and
                        // send the file again from the top, once.
                        restarted = true;
                        session = await openSession({ workspaceId, file, alias: alias || entry.alias, getIdToken });
                        update(entry.key, { uploadedBytes: 0, bytesPerSecond: 0, rateSampleAt: 0 });
                        continue;
                    }
                    throw err;
                }
            }
            await report(file.size, true);
            finish({ state: UPLOAD_UPLOADED, uploadedBytes: file.size });
        } catch (err) {
            if (err instanceof UploadAbortedError) {
                finish({ state: UPLOAD_CANCELLED, error: '' });
            } else {
                finish({ state: UPLOAD_ERROR, error: err.message || String(err) });
            }
        } finally {
            releaseSlot();
        }
    };
    run();

    return { key: entry.key, done };
}
